import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Host } from '../api/client';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface AuthContextValue {
  host: Host | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<Host>;
  register: (email: string, name: string, password: string) => Promise<Host>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const AUTH_QUERY_KEY = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Fetch current session on mount
  const {
    data: host,
    isLoading,
  } = useQuery<Host | null>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        return await api.auth.me();
      } catch (err) {
        // 401 simply means "not logged in" — not an error we should surface
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 min – session doesn't change often
    retry: false,
  });

  // Login
  const loginMutation = useMutation({
    mutationFn: (data: { email: string; password: string }) => api.auth.login(data),
    onSuccess: (user) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, user);
    },
  });

  const login = useCallback(
    (email: string, password: string) => loginMutation.mutateAsync({ email, password }),
    [loginMutation],
  );

  // Register
  const registerMutation = useMutation({
    mutationFn: (data: { email: string; name: string; password: string }) =>
      api.auth.register(data),
    onSuccess: (user) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, user);
    },
  });

  const register = useCallback(
    (email: string, name: string, password: string) =>
      registerMutation.mutateAsync({ email, name, password }),
    [registerMutation],
  );

  // Logout
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await api.auth.logout();
    },
    onSuccess: () => {
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
      queryClient.removeQueries();
    },
  });

  const logout = useCallback(() => logoutMutation.mutateAsync(), [logoutMutation]);

  const value = useMemo<AuthContextValue>(
    () => ({
      host: host ?? null,
      isLoading,
      isAuthenticated: !!host,
      login,
      register,
      logout,
    }),
    [host, isLoading, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
